import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  DROPZONE_FILE_LIMIT,
  DROPZONE_SIZE_ERROR,
  DROPZONE_TYPE_ERROR,
  ERROR_MSG,
  EVENT_TYPES,
  EVENTS,
} from "../consts/Common";
import { ATTACHMENT } from "../consts/DBFields";
import { trackEvent } from "../utils/appInsight";

/**
 * Custom hook to handle file uploads with dropzone
 * @param {Object} options Configuration options for the hook
 * @returns {Object} State and handlers for attachment uploads
 */
export const useFileUpload = ({
    files = [],
    maxFiles,
    accept,
    sizeLimit,
    handleUpload,
    clearError = () => {},
    multiple = false,
    getTotalFileSize = () => Promise.resolve(0),
    fileUploadStateRef = null,
    initialError = false,
    onRejected = null,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [fileUploaderError, setFileUploaderError] = useState("");
    const [fileNames, setFileNames] = useState([]);
    const [error, setError] = useState(initialError);

    // Update file names when files prop changes
    useEffect(() => {
        updateFileNames(files);
    }, [files]);

    // Connect the state setter to the ref if provided
    useEffect(() => {
        if (fileUploadStateRef) {
        fileUploadStateRef.current = setUploadComplete;
        }
    }, []);

    // Update uploading state when upload is complete or has errors
    useEffect(() => {
        if (uploadComplete || fileUploaderError || error) {
            setIsUploading(false);
        }
    }, [uploadComplete, fileUploaderError, error]);

    /**
     * Updates the list of file names based on the files array
     * @param {Array} files Array of file objects
     */
    const updateFileNames = (files) => {
        if (files?.length) {
            setFileNames(
                files.map((file) => (file.name ? file.name : file[ATTACHMENT.NAME]))
            );
            if (uploadComplete || fileUploaderError || error) {
                setIsUploading(false);
            }
        } else {
            // setFileNames([]);
        }
    };

    /**
     * Handles dropped files
     * @param {Array} currentFiles Current files array
     * @param {Array} acceptedFiles New files that were dropped
     */
    const onDrop = useCallback(
        async (currentFiles, acceptedFiles) => {
        setUploadComplete(false);
        clearError();
        setFileUploaderError("");

        if (acceptedFiles && acceptedFiles.length) {
            setIsUploading(true);

            // Check if max files limit would be exceeded
            if (
                maxFiles &&
                acceptedFiles.length + currentFiles?.length > maxFiles
            ) {
                setFileUploaderError(`You can only upload up to ${maxFiles} files at a time.`);
                setIsUploading(false);
                return;
            }

            // Check total file size if multiple uploads are allowed
            if (multiple && sizeLimit) {
                try {
                    let size = await getTotalFileSize();
                        acceptedFiles?.forEach((file) => {
                        size = size + file.size;
                    });

                if (size > sizeLimit) {
                    setFileUploaderError(ERROR_MSG.FILE_SIZE(sizeLimit / 1000000));
                    setIsUploading(false)
                    return;
                }
                } catch (err) {
                    setFileUploaderError(ERROR_MSG.FILES_UPLOAD);
                    setIsUploading(false)
                    return;
                }
            }

            // Handle the upload
            const isUploaded = await handleUpload(
                acceptedFiles,
                multiple ? currentFiles : undefined
            );
            if (!isUploaded) {
                setIsUploading(false);
                return;
            }

            // Update file names with all files (current + new)
            updateFileNames([
                ...(acceptedFiles?.length ? acceptedFiles : []),
                ...(multiple && currentFiles?.length ? currentFiles : []),
            ]);

            // Set upload complete
            if (!multiple) {
                setTimeout(() => {
                    setUploadComplete(true);
                }, 350);
            } else {
                setUploadComplete(true);
            }

            setFileUploaderError("");

            // Track the event
            trackEvent(
                EVENTS.USER_INTERACTION,
                null,
                EVENT_TYPES.USER_INTERACTION.CSV_UPLOAD
            );
        }
    },
    [handleUpload, maxFiles, multiple, sizeLimit, clearError, getTotalFileSize]
  );

  /**
   * Handles rejected files
   * @param {Array} events Rejection events
   */
    const onDropRejected = (events) => {
        onRejected?.(events);

        let errors = [];
        events?.forEach((event) => {
            if (event.errors.length) {
                errors.push(...event.errors);
            }
        });

        // Check for file count errors
        const fileCountError = errors.find(
            (err) => err.code === DROPZONE_FILE_LIMIT
        );
        if (fileCountError) {
            setFileUploaderError(`You can only upload up to ${maxFiles} files at a time.`);
            return;
        }

        // Check for file size errors
        const fileSizeError = errors.find(
            (err) => err.code === DROPZONE_SIZE_ERROR
        );
        if (fileSizeError) {
            setFileUploaderError(ERROR_MSG.FILE_SIZE(sizeLimit / 1000000));
            return;
        }

        // Check for file type errors
        const fileTypeError = errors.find(
            (err) => err.code === DROPZONE_TYPE_ERROR
        );
        if (fileTypeError) {
            setFileUploaderError(fileTypeError.message);
            return;
        }

        // Generic error
        setFileUploaderError("There was an error uploading your file.");
  };

    const onDropAccepted = () => {
        setIsUploading(true); // Set uploading immediately when files are accepted
    }

    // Initialize dropzone
    const dropzoneProps = useDropzone({
        onDrop: (acceptedFiles) => onDrop(files, acceptedFiles),
        onDropRejected,
        onDropAccepted,
        accept,
        maxSize: sizeLimit,
        maxFiles: multiple ? maxFiles : 1,
        multiple,
        disabled: isUploading,
    });

    const resetUploadState = () => {
        setUploadComplete(false);
        setFileUploaderError("");
        setIsUploading(false);
    };

    return {
        // State
        isUploading,
        uploadComplete,
        fileUploaderError,
        fileNames,

        // Actions
        setUploadComplete,
        setFileUploaderError,
        resetUploadState,
        updateFileNames,

        // Dropzone
        ...dropzoneProps,
    };
};
